import React from 'react';


const MBComparison = ({ data, days }) => {
  if (!data) {
    return (
      <div className="mb-comparison-container">
        <div className="loading-state">Loading M&B comparison...</div>
      </div>
    );
  }

  const {
    mb_response,
    salon_situation,
    if_matched_mb,
    cost_coverage_ratio,
    context_message
  } = data;

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value) => {
    return `${value.toFixed(1)}%`;
  };

  // Determine coverage status
  const getCoverageStatus = () => {
    if (cost_coverage_ratio >= 2.0) return 'excellent';
    if (cost_coverage_ratio >= 1.5) return 'good';
    if (cost_coverage_ratio >= 1.0) return 'adequate';
    return 'challenging';
  };

  const coverageStatus = getCoverageStatus();

  return (
    <div className="mb-comparison-container">
      {/* Header Section */}
      <div className="mb-comparison-header">
        <h2>The Mitchells & Butlers Reality Check</h2>
        <p className="mb-subtitle">
          How a pub chain faced 3.4× your cost pressure and adapted without government help
        </p>
      </div>

      {/* The Gut-Punch Section */}
      <div className="reality-check-banner">
        <div className="coverage-ratio-display">
          <span className="coverage-label">Your Cost Coverage Ratio:</span>
          <span className={`coverage-value ${coverageStatus}`}>
            {cost_coverage_ratio.toFixed(1)}×
          </span>
        </div>
        <p className="reality-message">{context_message}</p>
      </div>

      {/* Side-by-Side Comparison */}
      <div className="comparison-grid">
        {/* M&B Column */}
        <div className="comparison-column mb-column">
          <div className="column-header">
            <h3>Mitchells & Butlers</h3>
            <span className="company-type">Pub Chain, 2023</span>
          </div>
          
          <div className="comparison-metrics">
            <div className="metric-row">
              <span className="metric-label">Cost Increase:</span>
              <span className="metric-value">{formatCurrency(mb_response.cost_increase)}</span>
            </div>
            
            <div className="metric-row">
              <span className="metric-label">Pressure as % of Revenue:</span>
              <span className="metric-value highlighted">{formatPercent(mb_response.pressure_pct)}</span>
            </div>
            
            <div className="metric-row separator">
              <span className="metric-label">Response Strategy:</span>
            </div>
            
            <div className="metric-row indent">
              <span className="metric-label">Price Increase:</span>
              <span className="metric-value">{formatPercent(mb_response.price_increase_pct)}</span>
            </div>
            
            <div className="metric-row indent">
              <span className="metric-label">Efficiency Focus:</span>
              <span className="metric-value">Yes</span>
            </div>
            
            <div className="metric-row separator">
              <span className="metric-label">Result:</span>
            </div>
            
            <div className="metric-row result">
              <span className="metric-label">Profit Impact:</span>
              <span className="metric-value success">{mb_response.result}</span>
            </div>
          </div>
        </div>

        {/* Your Salon Column */}
        <div className="comparison-column salon-column">
          <div className="column-header">
            <h3>Your Salon</h3>
            <span className="company-type">Last {days} Days</span>
          </div>
          
          <div className="comparison-metrics">
            <div className="metric-row">
              <span className="metric-label">Estimated Annual Costs:</span>
              <span className="metric-value">{formatCurrency(salon_situation.annual_costs)}</span>
            </div>
            
            <div className="metric-row">
              <span className="metric-label">Cost Pressure (estimated):</span>
              <span className="metric-value highlighted">{formatPercent(salon_situation.pressure_pct)}</span>
            </div>
            
            <div className="metric-row separator">
              <span className="metric-label">Current Response:</span>
            </div>
            
            <div className="metric-row indent">
              <span className="metric-label">Recent Price Change:</span>
              <span className="metric-value">{salon_situation.recent_price_action || 'Unknown'}</span>
            </div>
            
            <div className="metric-row indent">
              <span className="metric-label">Efficiency Focus:</span>
              <span className="metric-value">{salon_situation.efficiency_focus || '?'}</span>
            </div>
            
            <div className="metric-row separator">
              <span className="metric-label">Current Situation:</span>
            </div>
            
            <div className="metric-row result">
              <span className="metric-label">Available Opportunities:</span>
              <span className="metric-value">{formatCurrency(salon_situation.opportunities_available)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* If You Matched M&B Section */}
      <div className="matched-mb-section">
        <h3>If You Matched M&B's Approach</h3>
        
        <div className="matched-mb-grid">
          <div className="matched-metric">
            <span className="matched-label">Revenue Increase:</span>
            <span className="matched-value">{formatCurrency(if_matched_mb.revenue_increase)}</span>
            <span className="matched-detail">
              ({formatPercent(mb_response.price_increase_pct)} price increase)
            </span>
          </div>
          
          <div className="matched-metric">
            <span className="matched-label">From Efficiency Gains:</span>
            <span className="matched-value">{formatCurrency(if_matched_mb.efficiency_gains)}</span>
            <span className="matched-detail">
              (Based on available opportunities)
            </span>
          </div>
          
          <div className="matched-metric highlight">
            <span className="matched-label">Total Additional Revenue:</span>
            <span className="matched-value total">{formatCurrency(if_matched_mb.total_additional)}</span>
          </div>
          
          <div className="matched-metric">
            <span className="matched-label">Estimated Cost Coverage:</span>
            <span className="matched-value success">{formatPercent(if_matched_mb.cost_coverage_pct)}</span>
          </div>
        </div>

        <div className="mb-conclusion">
          <p className="conclusion-text">
            M&B faced a <strong>{formatPercent(mb_response.pressure_pct)}</strong> cost pressure.
            You're facing <strong>{formatPercent(salon_situation.pressure_pct)}</strong>.
            They absorbed it through a modest price increase and efficiency improvements.
            <strong className="conclusion-highlight"> You're in a better position than they were.</strong>
          </p>
        </div>
      </div>

      {/* The Message Section */}
      <div className="mb-message-box">
        <h4>The Reality</h4>
        <ul className="reality-points">
          <li>M&B didn't wait for government intervention</li>
          <li>They didn't lobby for tax cuts</li>
          <li>They didn't claim they were helpless victims</li>
          <li>They made a <strong>3.2% price increase</strong> and focused on <strong>efficiency</strong></li>
          <li>Result: <strong>20% profit growth</strong></li>
        </ul>
        
        <p className="final-message">
          Your cost pressure is <strong>lower</strong>. Your opportunities are <strong>visible</strong>. 
          Your data is <strong>real</strong>. No VAT cut needed. No lobbying required. No waiting.
        </p>
      </div>

      {/* Data Source Note */}
      <div className="data-source-note">
        <p>
          <strong>M&B Data Source:</strong> Mitchells & Butlers PLC Annual Report 2023. 
          Cost increase: £130m on revenue of ~£2.75bn. Response documented in shareholder communications.
        </p>
        <p>
          <strong>Your Data:</strong> Calculated from your actual employee_income records over the last {days} days, 
          annualised. Cost estimates based on industry standards (where actual expenditure data not available).
        </p>
      </div>
    </div>
  );
};

export default MBComparison;